import React, { PropsWithChildren } from 'react';
import { useMutation } from '@apollo/client/react/hooks';
import FormGroup from '@mui/material/FormGroup';
import { Field, FieldProps, Formik } from 'formik';
import { gql } from '@yi/core';
import { i18n_a_link_to_reset_your_password_has_been_sent } from '@yi/i18n/phrases/i18n_a_link_to_reset_your_password_has_been_sent';
import { i18n_check_your_email } from '@yi/i18n/phrases/i18n_check_your_email';
import { i18n_email } from '@yi/i18n/phrases/i18n_email';
import { i18n_email_address } from '@yi/i18n/phrases/i18n_email_address';
import { i18n_forgot_your_password } from '@yi/i18n/phrases/i18n_forgot_your_password';
import { i18n_reset_password } from '@yi/i18n/phrases/i18n_reset_password';
import { i18n_your_email_address } from '@yi/i18n/phrases/i18n_your_email_address';

import { useMaybeRedirectAfterPasswordReset } from '~/src/common/sign-in';
import { CircularProgress } from '~/src/components/buttons/button-styles';
import { FormInput } from '~/src/components/FormInput';
import { useLocale } from '~/src/components/LocaleProvider';

import { Wrapper, FormBody } from './elements';
import { Heading, Button } from '../elements';

interface Props {
  emailAddress: string;
}

export const ForgotPasswordForm: React.FC<PropsWithChildren<Props>> = ({ emailAddress }) => {
  const { locale } = useLocale();
  const [sendForgotPasswordRequest] = useMutation<
    gql.typesClient.Mutate_ForgotPasswordRequest,
    gql.typesClient.Mutate_ForgotPasswordRequestVariables
  >(gql.Mutate_ForgotPasswordRequest, {
    // If graphQL error, onError is necessary for mutation promise to resolve
    onError: error => {
      console.warn(error);
    },
  });

  const { planToRedirectToCurrentPageAfterPasswordReset } = useMaybeRedirectAfterPasswordReset();

  return (
    <Formik
      initialValues={{
        email: emailAddress ? emailAddress : '',
      }}
      onSubmit={async (values, { setStatus, setSubmitting }) => {
        const variables: gql.typesClient.Mutate_ForgotPasswordRequestVariables = {
          email: values.email,
        };
        const res = await sendForgotPasswordRequest({ variables });
        setSubmitting(false);
        // handle mutation error
        if (res?.data?.forgotPasswordRequest?.errors) {
          setStatus({ error: res.data.forgotPasswordRequest.errors });
        } else if (!res) {
          // handle GraphQl error
          setStatus({ error: 'Server Error' });
        } else {
          setStatus({
            submitted: `${i18n_check_your_email(
              locale,
            )} ${i18n_a_link_to_reset_your_password_has_been_sent(locale)}`,
          });
          planToRedirectToCurrentPageAfterPasswordReset();
        }
      }}
    >
      {({ isSubmitting, handleSubmit, status }) => (
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Wrapper id="forgot-password-form-wrapper">
            <Heading>{i18n_forgot_your_password(locale)}</Heading>
            {status?.submitted && (
              <p style={{ marginTop: '8px', marginBottom: 0 }}>{status?.submitted}</p>
            )}
            <FormBody>
              <Field
                label={i18n_email_address(locale)}
                type="email"
                name="email"
                render={({ field }: FieldProps<{ email?: string }>) => (
                  <FormGroup style={{ width: '100%', display: status?.submitted && 'none' }}>
                    <FormInput
                      pt={1}
                      pb={3}
                      {...field}
                      autoFocus
                      label={i18n_your_email_address(locale)}
                      placeholder={i18n_email(locale)}
                      type="email"
                      id="forgot-email"
                      name="email"
                      data-required="true"
                      required
                      variant="filled"
                      error={status && Boolean(status.error)}
                      helperText={status?.error}
                    />

                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting && <CircularProgress spinnerColor="white" size={24} />}
                      {i18n_reset_password(locale)}
                    </Button>
                  </FormGroup>
                )}
              />
            </FormBody>
          </Wrapper>
        </form>
      )}
    </Formik>
  );
};
